import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { ShipmentRowInterface } from '../../common/types'
import { SHIPMENT_STATUS } from '../../common/constants'
import { isEmpty, sortBy } from 'lodash'
import {
  isFailedValidation,
  reverseShipmentConstraints,
} from '../PurchaseOrders/utils'
import { BusinessUnit } from '../Modal/CreateReverseShipment/types/ReverseShipmentTypes'

const FDC_LOCATIONS = [
  '3712',
  '3727',
  '3735',
  '3880',
  '3881',
  '3892',
  '3895',
  '3897',
  '3899',
]

const CONSOL_VENDOR_IDS = [4940324, 4940993, 4941307, 4941460, 4966276]

dayjs.extend(utc)
dayjs.extend(timezone)

export const renderSelectAllBox = (
  shipments: Record<string, ShipmentRowInterface>,
) => {
  return !!Object.values(shipments).find(
    (row) =>
      row.currentShipmentStatus === SHIPMENT_STATUS.CONTENT_REVIEW_REQUIRED,
  )
}

export const sortedIndexStatus: { [key: string]: number } = {
  'Past Pickup': 0,
  'Content Review Required': 1,
  'On Hold for Processing': 2,
  'Retrieving Pickup Date': 3,
  'Routing In Progress': 4,
  'Awaiting Carrier Acceptance': 5,
  'Carrier Accepted, Awaiting Pickup': 6,
  Cancelled: 7,
  'Picked Up': 8,
}

export const sortShipments = (shipments: Array<ShipmentRowInterface>) => {
  return sortBy(shipments, [
    (shipment: ShipmentRowInterface) => {
      return sortedIndexStatus[shipment.currentShipmentStatus]
    },
  ])
}

export const isFDC = (destLocCode: string): boolean =>
  FDC_LOCATIONS.includes(destLocCode)

export const isCONSOL = (vendorId: number): boolean =>
  CONSOL_VENDOR_IDS.includes(vendorId)

export const getPickupDate = (
  destinationLocationCode: string,
  todaysDate: string,
  allowZeroLeadTime: boolean = false,
) => {
  return isFDC(destinationLocationCode)
    ? { date: todaysDate, offsetDays: 1 }
    : {
        date: todaysDate,
        offsetDays: !allowZeroLeadTime ? 2 : 0,
      }
}

export const shipmentAttributes = {
  appointmentNumber: null,
  bol: null,
  carton: null,
  commodityCode: null,
  totalVol: null,
  destLocCode: null,
  pallet: null,
  irdDate: null,
  pickupDate: null,
  totalWt: null,
}

const reverseAttributes = {
  reverseLogisticsReferenceNumber: null,
  returnAuthNumber: null,
  sealNumber: null,
  trailerId: null,
}

export const isValidProperties = (
  dimensions: any,
  attributes: Record<string, any> = shipmentAttributes,
  reverse: boolean = false,
): boolean =>
  Object.keys(reverse ? { ...attributes, ...reverseAttributes } : attributes)
    .map((ele) =>
      isValidProperty(
        ele,
        dimensions?.[ele] || '',
        isFDC(dimensions?.['destLocCode']?.toString()),
        dimensions?.['shipmentType'] === 'REVERSE_LOGISTICS_MANUAL'
          ? false
          : true,
        dimensions?.['shipmentType'] === 'REVERSE_LOGISTICS_MANUAL',
        dimensions?.['businessUnit'],
      ),
    )
    .every(Boolean)

export const isValidProperty = (
  field: string,
  fieldValue: any,
  isFDC: boolean = false,
  cartonRequired: boolean = true,
  isReverseShipment: boolean = false,
  reverseBusinessUnit: BusinessUnit | undefined = undefined,
): boolean => {
  switch (field) {
    case 'commodityCode':
    case 'destLocCode':
      return !isEmpty(fieldValue?.toString())
    case 'bol':
    case 'appointmentNumber':
      return (
        isEmpty(fieldValue) || !isFailedValidation(fieldValue, field, isFDC)
      )
    case 'reverseLogisticsReferenceNumber':
      return (
        isEmpty(fieldValue) ||
        validReverseLogisticsReferenceNumber(fieldValue, reverseBusinessUnit)
      )
    case 'returnAuthorizationNumber':
      return (
        isEmpty(fieldValue) ||
        !isFailedValidation(fieldValue, field, isFDC, isReverseShipment)
      )
    case 'totalVol':
      return (
        fieldValue > 0 &&
        !isFailedValidation(fieldValue, field, isFDC, isReverseShipment)
      )
    case 'totalWt':
    case 'pallet':
      return (
        fieldValue > 0 &&
        !isFailedValidation(fieldValue, field, isFDC, isReverseShipment)
      )
    case 'carton':
      if (cartonRequired) {
        return fieldValue > 0 && !isFailedValidation(fieldValue, field, isFDC)
      } else return true
    case 'trailerId':
      return isEmpty(fieldValue) || validTrailerIdandSealNumber(fieldValue)
    case 'sealNumber':
      return isEmpty(fieldValue) || validTrailerIdandSealNumber(fieldValue)
    default:
      return !isFailedValidation(fieldValue, field, isFDC)
  }
}

const validReverseLogisticsReferenceNumber = (
  fieldValue: string,
  businessUnit: BusinessUnit | undefined,
): boolean => {
  if (
    businessUnit !== undefined &&
    [
      BusinessUnit.RCtoRTV,
      BusinessUnit.RCtoTargetPlus,
      BusinessUnit.RCtoHQIR,
    ].includes(businessUnit)
  ) {
    return (
      !isEmpty(fieldValue) &&
      reverseShipmentConstraints.rtvsNumber.format.pattern.test(fieldValue)
    )
  } else if (
    businessUnit !== undefined &&
    [
      BusinessUnit.RCtoREBELSTORK,
      BusinessUnit.RCtoContractedB2B,
      BusinessUnit.RCtoUMB,
      BusinessUnit.RCtoCircularRecycling,
    ].includes(businessUnit)
  ) {
    return (
      !isEmpty(fieldValue) &&
      reverseShipmentConstraints.wsNumber.format.pattern.test(fieldValue)
    )
  } else {
    return isEmpty(fieldValue)
  }
}

// Alphanumeric minimum 2 and maximum 12 characters
const validTrailerIdandSealNumber = (fieldValue: string): boolean => {
  return (
    /^[a-zA-Z0-9]+$/.test(fieldValue) &&
    fieldValue.length >= 2 &&
    fieldValue.length <= 12
  )
}
